import { isCollaboratorClientType } from "../constant";

// rowData shape differs by caller: ContractTableMain.js/ParentChildTable.jsx
// precompute `client`/`collaborator`/`clientType` as plain strings on the row;
// ParentTable.jsx does not, leaving the raw nested `eventClients`/`clientType`
// objects. Check both.
const resolveClientTypeName = (rowData) =>
  typeof rowData?.clientType === "string" ? rowData.clientType : rowData?.clientType?.name;

const resolveContractRecipientName = (rowData) => {
  const isCollaborator = isCollaboratorClientType(resolveClientTypeName(rowData));
  if (isCollaborator) {
    return rowData?.collaborator || rowData?.eventClients?.[0]?.collaborator?.name || "the collaborator";
  }
  return rowData?.client || rowData?.eventClients?.[0]?.client?.name || "the client";
};

/**
 * Confirmation gate for deleting a contract/event.
 *
 * Deleting an event whose contract is out for signature (`contractStatus ===
 * "Sent"`) cancels the live signature request at the provider (#272) — the
 * client's link goes dead. That's a real consequence, not just row cleanup,
 * so it has to be named up front rather than discovered after the fact.
 *
 * @param {object} modal  The `Modal.useModal()` instance from the caller's
 *   own component (each table already owns one — this isn't a hook, just a
 *   function that uses it, so callers keep their existing hook/context).
 * @param {object} rowData  The table row being deleted.
 * @param {object} options
 * @param {Function} options.onConfirm  Runs only when the user confirms.
 * @param {boolean} [options.hasChildren]  Appends the child-entries warning.
 */
const confirmDeleteContract = (modal, rowData, { onConfirm, hasChildren = false } = {}) => {
  const isOutForSignature = rowData?.contractStatus === "Sent";
  const recipientName = resolveContractRecipientName(rowData);

  modal.confirm({
    title: "Delete Event",
    content: (
      <span>
        Would you like to remove this event?
        {hasChildren && " Doing so will also remove all associated child entries."}
        {isOutForSignature && (
          <>
            {" "}
            <strong>This will cancel the signature request sent to {recipientName}.</strong>
          </>
        )}
      </span>
    ),
    okText: "Yes",
    cancelText: "Cancel",
    onOk: onConfirm,
  });
};

export default confirmDeleteContract;
